import React from "react";
import {
  FaShieldAlt,
  FaUserFriends,
  FaUniversity,
  FaCogs,
  FaHandHoldingUsd,
  FaLock,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowLeft } from "react-icons/fa6";

function FaqPage() {
  const faqCategories = [
    {
      icon: FaShieldAlt,
      color: "indigo",
      title: "Security & Privacy",
      questions: [
        {
          q: "How secure is my financial data?",
          a: "We use bank-level encryption and never store your credentials. All data is encrypted using AES-256, the same security standard used by major financial institutions.",
        },
        {
          q: "Who can access our family's financial information?",
          a: "Only you and your spouse have access to your family's dashboard. Each person has their own secure login credentials and all access is logged.",
        },
        {
          q: "What data do you store vs. just display?",
          a: "We only store basic account information needed to show your dashboard. Your bank credentials are never stored—they're securely handled by Plaid.",
        },
        {
          q: "Is the app compliant with data protection regulations like GDPR or CCPA?",
          a: "We are committed to data privacy and comply with relevant data protection regulations, including GDPR and CCPA. You can read more about our privacy policy on our website.",
        },
        {
          q: "How does the app handle privacy between family members?",
          a: "Currently, all connected accounts are visible to both you and your spouse to provide a consolidated financial view. In future updates, we plan to introduce privacy controls that allow you to manage which accounts or transactions are shared.",
        },
        {
          q: "How secure is the connection to my bank accounts?",
          a: "We use Plaid's secure integration to connect to your bank accounts. Your credentials are handled securely and are never stored on our servers. All data transmissions are encrypted using industry-standard protocols.",
        },
      ],
    },
    {
      icon: FaUserFriends,
      color: "blue",
      title: "Family Access",
      questions: [
        {
          q: "Can I add more than one family member to our account?",
          a: "At this time, the app allows you to add your spouse as an additional family member. Support for adding more family members beyond your spouse is not included in the MVP but may be considered in future releases.",
        },
        {
          q: "Can I add my spouse to the account?",
          a: "Yes! Your spouse gets their own secure login while sharing access to your consolidated financial dashboard.",
        },
        {
          q: "What can my spouse see and do in the account?",
          a: "Both you and your spouse have equal access to view all connected accounts and balances. Account connections can be managed by either person.",
        },
        {
          q: "How do we manage separate login credentials?",
          a: "Each person uses their own Google account to sign in, maintaining individual secure access while sharing the family dashboard.",
        },
        {
          q: "How does the app handle shared accounts that both my spouse and I have connected?",
          a: "Our system intelligently identifies and merges shared accounts connected by multiple family members to avoid duplication of transactions and account balances.",
        },
        {
          q: "How do I invite my spouse to join the family dashboard?",
          a: "You can invite your spouse by sending an invitation via email from the Family section of your account. Your spouse will receive an email with instructions to accept the invitation and set up their account.",
        },
        {
          q: "How does the app handle privacy between family members?",
          a: "Currently, all connected accounts are visible to both you and your spouse. We're working on adding privacy controls to allow users to manage which accounts or transactions are shared.",
        },
      ],
    },
    {
      icon: FaUniversity,
      color: "green",
      title: "Bank Connections",
      questions: [
        {
          q: "Which banks do you support?",
          a: "We support over 11,000 banks across the US, Canada, UK, and Australia through our partnership with Plaid, including Chase, RBC, Barclays, Commonwealth Bank, and many more.",
        },
        {
          q: "What if my bank isn't listed in the supported banks?",
          a: "If your bank isn't listed, please contact our support team, and we'll work on adding it to our network.",
        },
        {
          q: "Can I connect business accounts as well?",
          a: "Yes, you can connect both personal and business accounts to get a complete view of your finances.",
        },
        {
          q: "Can I connect investment accounts to the dashboard?",
          a: "Currently, our focus is on providing a consolidated view of your bank accounts. Investment account support is not available in the MVP version, but we're planning to include investment tracking in future updates.",
        },
        {
          q: "How often is account information updated?",
          a: "Account balances and transactions are updated in real-time whenever you access your dashboard, ensuring you always have the most current view of your finances.",
        },
        {
          q: "How secure is the connection to my bank accounts?",
          a: "We use Plaid's secure integration to connect to your bank accounts. Your credentials are handled securely and are never stored on our servers. All data transmissions are encrypted using industry-standard protocols.",
        },
      ],
    },
    {
      icon: FaCogs,
      color: "purple",
      title: "Technical Details",
      questions: [
        {
          q: "How long does setup take?",
          a: "Most families connect their accounts in under 2 minutes. Simply sign in with Google and connect your banks through our secure Plaid integration.",
        },
        {
          q: "What happens if a bank connection breaks?",
          a: "We automatically notify you if any connection needs attention, with clear steps to quickly reconnect your account.",
        },
        {
          q: "Can I export my transaction data?",
          a: "Yes, you can export your account balances and transactions in common formats like CSV for your records or further analysis.",
        },
        {
          q: "Is there a mobile app available?",
          a: "We are currently offering a web-only application optimized for desktop and mobile browsers. A dedicated mobile app is not part of the MVP but may be developed in the future.",
        },
        {
          q: "Does the app support multiple currencies?",
          a: "Currently, the app supports accounts in the currencies provided by the connected banks. All amounts are displayed in the currency reported by your bank accounts.",
        },
        {
          q: "What if I forget my password or can't access my account?",
          a: "Since we use Google Sign-In for authentication, you can recover your account access through Google's account recovery process. Ensure that your Google account recovery options are up to date.",
        },
      ],
    },
    {
      icon: FaHandHoldingUsd,
      color: "pink",
      title: "Features & Functionality",
      questions: [
        {
          q: "Does the app support budgeting or financial goal-setting features?",
          a: "The MVP focuses on providing a clear view of your family's finances by consolidating all accounts in one place. Budgeting and goal-setting features are not included at this stage but are on our roadmap for future enhancements.",
        },
        {
          q: "Can I customize my dashboard view?",
          a: "At this time, customization options are limited. We're working on features that will allow you to personalize your dashboard by selecting which accounts or financial metrics are displayed.",
        },
        {
          q: "Is there a way to get notifications for large transactions or unusual activity?",
          a: "Notification features are not included in the MVP. We're exploring adding notification preferences in future versions so you can stay informed about important account activities.",
        },
      ],
    },
    {
      icon: FaLock,
      color: "orange",
      title: "Account & Support",
      questions: [
        {
          q: "Are there any fees to use the app?",
          a: "The app is currently free to use during the beta period. Pricing and subscription models will be announced upon official launch.",
        },
        {
          q: "How can I provide feedback or request new features?",
          a: "We value your feedback! Please reach out to our support team at support@omnibutler.com with your comments or feature requests.",
        },
        {
          q: "Do you offer customer support if I encounter issues?",
          a: "Yes, our support team is here to help you with any questions or issues you may have. Contact us at support@omnibutler.com, and we'll assist you promptly.",
        },
        {
          q: "Will you add more features in the future?",
          a: "Absolutely! We're continuously working to improve the app and plan to add features like budgeting tools, investment tracking, and more family management options based on user feedback.",
        },
      ],
    },
  ];

  return (
    <div className="py-24 bg-white dark:bg-slate-900">
      <div className="container">
        {/* Back to Home Link */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium transition-colors duration-300 group"
          >
            <FaArrowLeft className="mr-2 text-sm transition-transform duration-300 group-hover:-translate-x-1" />
            Back to Home
          </Link>
        </div>

        <h1 className="text-4xl font-bold mb-16 text-center">
          Frequently Asked Questions
        </h1>

        <div className="grid md:grid-cols-2 gap-8">
          {faqCategories.map((category, index) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="group bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center space-x-4 mb-4">
                  <category.icon
                    className={`w-6 h-6 text-${category.color}-600 transition-transform duration-300 group-hover:scale-110`}
                  />
                  <h2 className="text-xl font-semibold">{category.title}</h2>
                </div>

                <div className="space-y-6">
                  {category.questions.map((faq, idx) => (
                    <div key={faq.q}>
                      <h3 className="font-semibold text-lg mb-2">{faq.q}</h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        {faq.a}
                      </p>
                      {idx < category.questions.length - 1 && (
                        <hr className="my-4 border-gray-200 dark:border-gray-700" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default FaqPage;
